import PropTypes from 'prop-types';
import IconButton, { ICON_BUTTON_ICONS, ICON_BUTTON_TYPES } from '../../../buttons/IconButton';
import { formatBeneficialOwnership } from './shareholderGraphUtils';
import IndependentShareholderNote from './IndependentShareholderNote';
import { linkedRowProps, rowKeyOf } from './linkedRowProps';
import ShowInGraphButton from './ShowInGraphButton';
import { GRAPH_COPY } from './graph/graphConstants';
import { canNavigateToNodeDetails } from './graph/graphCardModel';
import { PUNCTUATION } from '../../../../utils/constants/notation';
import { FIELD_SOURCE, dataSourceClass } from '../../../../utils/constants/dataSources';
import { openInNewTabButtonProps } from '../../../../utils/openInNewTab';

// Rows are the graph's own nodes: the same gate as the cards and the new-tab handler.
const canViewDetails = (entry) => canNavigateToNodeDetails(entry);

/**
 * Get appropriate tooltip text based on entity type and navigability
 */
const getTooltipText = (entry) => {
  if (entry?.type === 'person') {
    return canViewDetails(entry) ? 'View person details' : 'Details not available';
  }
  if (entry?.type === 'company' || entry?.type === 'subsidiary') {
    return canViewDetails(entry) ? 'View company details' : 'Details not available';
  }
  return 'Details not available for this entity';
};

// Without isInGraph every row counts as drawn.
const everyRow = () => true;

/**
 * UltimateShareholdersTable - Displays list of ultimate shareholders
 *
 * @param {Object} props
 * @param {Array} props.shareholders - Array of shareholder entries with member_name, beneficial_ownership, age
 * @param {boolean} props.isIndependent - Whether company has no majority shareholder
 * @param {string} props.companyName - Company name for independent message
 * @param {boolean} props.isPhoneOrTablet - Device type for styling
 * @param {string} props.layout - Layout mode ('desktop', 'tablet', 'mobile')
 * @param {number} props.cardHeight - Parent card height for dynamic sizing
 * @param {Function} props.onViewDetails - Callback when view details is clicked (TD-1709)
 * @param {Function} [props.onOpenDetailsInNewTab] - Opens the entry's details in a new tab
 *   (Cmd/Ctrl-click and the right-click menu on the view-details icon)
 * @param {Function} [props.onHoverShareholder] - (entry | null) => the row under the pointer or
 *   holding the focus, for the graph above to highlight that owner; with it the rows show a
 *   hover state
 * @param {Function} [props.isInGraph] - (entry) => whether the graph draws the row's card; only
 *   those rows are linked (default: every row)
 * @param {Function} [props.onShowInGraph] - (entry) => reveals a card the graph does not draw;
 *   with it such rows offer 'Show in graph'
 */
const UltimateShareholdersTable = ({
  shareholders = [],
  isIndependent = false,
  companyName = '',
  isPhoneOrTablet = false,
  layout = 'desktop',
  cardHeight = 0,
  onViewDetails,
  onOpenDetailsInNewTab,
  onHoverShareholder,
  isInGraph = everyRow,
  onShowInGraph
}) => {
  /**
   * CALCULATION OWNERSHIP: This component does NOT calculate - uses value directly
   * - Receives: cardHeight (already calculated as (measuredHeight - 20) / 2 by parent)
   * - Uses: cardHeight directly without modification
   * - DO NOT apply division or calculation here - parent owns the calculation
   */
  const dynamicStyle =
    layout === 'desktop' && cardHeight > 0
      ? {
          minHeight: `${cardHeight}px`,
          maxHeight: `${cardHeight}px`
        }
      : {};

  return (
    <div className="card" style={dynamicStyle}>
      <div className={`heading ${dataSourceClass(FIELD_SOURCE.shareholders)}`}>
        Ultimate shareholder(s)
      </div>
      <div className="ultimate-header">
        <div className="item share">Share (%)</div>
        <div className="item name">Name</div>
        <div className="item age">Age</div>
        {onViewDetails && <div className="item actions"></div>}
      </div>
      <div className={`entities-list${isPhoneOrTablet ? '' : ' scrollbar-styles'}`}>
        {!!shareholders?.length &&
          shareholders.map((entry, index) => {
            const inGraph = isInGraph(entry);
            const onHoverRow = inGraph ? onHoverShareholder : undefined;
            return (
              <div
                className={`ultimate-row${onHoverRow ? ' ultimate-row--linked' : ''}`}
                key={rowKeyOf(entry, index)}
                {...linkedRowProps(entry, onHoverRow)}
              >
                <div className="item share">{formatBeneficialOwnership(entry)}</div>
                <div className="item name">{entry.member_name}</div>
                <div className="item age">{entry.age || PUNCTUATION.EMPTY_VALUE}</div>
                {onViewDetails && (
                  <div className="item actions">
                    {!inGraph && onShowInGraph && (
                      <ShowInGraphButton
                        entry={entry}
                        onShow={onShowInGraph}
                        hint={GRAPH_COPY.SHOW_OWNER_IN_GRAPH_HINT}
                      />
                    )}
                    <IconButton
                      wrapClassName="view-details-btn"
                      icon={ICON_BUTTON_ICONS.VIEW_DETAILS_CUSTOM1}
                      type={ICON_BUTTON_TYPES.BLUE}
                      {...openInNewTabButtonProps({
                        activate: () => onViewDetails(entry),
                        openInNewTab: onOpenDetailsInNewTab && (() => onOpenDetailsInNewTab(entry)),
                        enabled: canViewDetails(entry)
                      })}
                      tooltipText={getTooltipText(entry)}
                      disabled={!canViewDetails(entry)}
                    />
                  </div>
                )}
              </div>
            );
          })}
      </div>
      {isIndependent && <IndependentShareholderNote companyName={companyName} />}
    </div>
  );
};

UltimateShareholdersTable.propTypes = {
  shareholders: PropTypes.array,
  isIndependent: PropTypes.bool,
  companyName: PropTypes.string,
  isPhoneOrTablet: PropTypes.bool,
  layout: PropTypes.string,
  cardHeight: PropTypes.number,
  onViewDetails: PropTypes.func,
  onOpenDetailsInNewTab: PropTypes.func,
  onHoverShareholder: PropTypes.func,
  isInGraph: PropTypes.func,
  onShowInGraph: PropTypes.func
};

export default UltimateShareholdersTable;
